import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  AP_INVOICE_EXCEPTION_STATUSES,
  APInvoiceStatus,
  TASK_STATUSES,
} from "@erp/shared-types";

/**
 * AP Command Center RPC Contract
 *
 * Dashboard of configurable "big number" metric tiles plus the exception
 * work queue. Each metric is defined per organization (stored in
 * `core_org_ui_section_configs`, falling back to built-in defaults) and is
 * derived from one of:
 *
 *  - `tasks`: AP tasks of given types/statuses, resolved to the invoices
 *    they anchor to (subtasks resolve through their parent's anchor)
 *  - `taskCount`: a count of tasks of any one task type. Clicking the tile
 *    opens that type's task page when one exists
 *  - `invoices`: `ap_invoices` filtered directly (status, overdue, amount)
 *  - `exceptions`: `ap_invoice_exceptions` rows resolved to their invoices
 *
 * Invoice, task, and exception metrics resolve to a concrete set of
 * invoices, which is what `drillDown` returns when a user clicks those
 * tiles. A `taskCount` tile counts tasks and navigates to the task page
 * instead.
 *
 * Flow tiles (throughput: purchase-matched, paid, …) opt into a time window
 * via optional `timeWindow` on the metric (sibling of `source`, so OpenAPI
 * keeps `source` as tasks | invoices). The page-level `range` selector
 * (7d / MTD / 30d / all) is applied to those tiles; backlog tiles omit
 * `timeWindow` and stay open until resolved.
 */

/** Page-level range the Command Center selector writes into the URL. */
export const AP_COMMAND_CENTER_RANGES = ["7d", "mtd", "30d", "all"] as const;
export const ApCommandCenterRangeSchema = z.enum(AP_COMMAND_CENTER_RANGES);
export const DEFAULT_AP_COMMAND_CENTER_RANGE =
  "30d" satisfies (typeof AP_COMMAND_CENTER_RANGES)[number];

export const AP_METRIC_TASK_TIME_WINDOW_FIELDS = [
  "createdAt",
  "completedAt",
] as const;
export const AP_METRIC_INVOICE_TIME_WINDOW_FIELDS = [
  "createdAt",
  "invoiceDate",
  "receivedAt",
  "dueDate",
  "paidAt",
] as const;
export const AP_METRIC_EXCEPTION_TIME_WINDOW_FIELDS = [
  "detectedAt",
  "resolvedAt",
] as const;
export const AP_METRIC_TIME_WINDOW_FIELDS = [
  ...AP_METRIC_TASK_TIME_WINDOW_FIELDS,
  "invoiceDate",
  "receivedAt",
  "dueDate",
  "paidAt",
  ...AP_METRIC_EXCEPTION_TIME_WINDOW_FIELDS,
] as const;

/**
 * Per-tile bound on the metric definition (not inside `source`, so the
 * tasks | invoices anyOf stays bitwise stable). `page` honors the Command
 * Center range selector; `lastDays` is a fixed rolling window. Omit
 * `timeWindow` for unbounded backlog tiles. `lastDays` is optional on the
 * wire; the UI and `saveMetricsConfig` require it when `mode` is `lastDays`.
 * `on` is the union of task and invoice fields; save/UI restrict it to the
 * source kind.
 */
export const ApMetricTimeWindowSchema = z.object({
  mode: z.enum(["page", "lastDays"]),
  on: z.enum(AP_METRIC_TIME_WINDOW_FIELDS),
  lastDays: z.int().min(1).max(3650).optional(),
});

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

/** Tasks-derived metric: tasks of these types resolve to anchored invoices. */
export const ApMetricTaskSourceSchema = z.object({
  kind: z.literal("tasks"),
  /** Task type keys, e.g. `ap-process-invoice` */
  taskTypeKeys: z.array(z.string().min(1)).min(1),
  /** Task statuses to match; omit to match any status */
  statuses: z.array(z.enum(TASK_STATUSES)).optional(),
  /** Only tasks carrying a tag with this prefix, e.g. `exception:` */
  tagPrefix: z.string().min(1).optional(),
  /** Only tasks with no assigned user */
  unassignedOnly: z.boolean().optional(),
  /**
   * Only tasks that are their chain's current stage: every earlier step of
   * the parent's checklist is complete. Distinguishes "approval is actually
   * up next" from checklists that pre-create all subtasks as pending.
   */
  currentStageOnly: z.boolean().optional(),
});

/**
 * Task-count metric: the number of tasks of one type, any anchor.
 * Unlike `tasks`, this does not resolve to invoices. The tile opens the
 * task page pinned to `taskTypeKey` (Settings → Organization → Task pages).
 * Creating that page is the link; its sidebar section only chooses where
 * the page is listed.
 */
export const ApMetricTaskCountSourceSchema = z.object({
  kind: z.literal("taskCount"),
  /** Any org task type key, not only types anchored to an AP invoice */
  taskTypeKey: z.string().min(1),
  /** Task statuses to match; omit to match any status */
  statuses: z.array(z.enum(TASK_STATUSES)).optional(),
});

/** Invoice-derived metric: filters `ap_invoices` directly. */
export const ApMetricInvoiceSourceSchema = z.object({
  kind: z.literal("invoices"),
  /** Invoice statuses to match; omit to match any non-void status */
  statuses: z.array(z.enum(APInvoiceStatus)).optional(),
  /** Only invoices past their due date */
  overdueOnly: z.boolean().optional(),
  /** Only invoices with total at or above this amount */
  minAmount: DecimalStringSchema.optional(),
});

/**
 * Exception-derived metric: `ap_invoice_exceptions` rows (opened by the
 * match node's evaluation) resolved to their invoices. This is how the
 * exception framework shows up on the dashboard — a source like any other,
 * not a hardcoded section.
 */
export const ApMetricExceptionSourceSchema = z.object({
  kind: z.literal("exceptions"),
  /** Exception statuses to match; omit = open only */
  statuses: z.array(z.enum(AP_INVOICE_EXCEPTION_STATUSES)).optional(),
  /** Only these exception types (Core Enum option ids); omit = any */
  exceptionTypeOptionIds: z.array(z.uuid()).optional(),
  /** Only exceptions no rule routed to an owner */
  unassignedOnly: z.boolean().optional(),
  /** Only exceptions open at least this many hours */
  minAgeHours: z
    .int()
    .min(1)
    .max(24 * 365)
    .optional(),
});

export const ApMetricSourceSchema = z.discriminatedUnion("kind", [
  ApMetricTaskSourceSchema,
  ApMetricTaskCountSourceSchema,
  ApMetricInvoiceSourceSchema,
  ApMetricExceptionSourceSchema,
]);

export const ApMetricToneSchema = z.enum([
  "default",
  "danger",
  "warning",
  "success",
]);

/**
 * A value-driven color rule: when the tile's displayed value (count or
 * amount, per `display`) satisfies the comparison, the tile takes `tone`.
 * Rules are evaluated in order; the first match wins.
 */
export const ApMetricColorRuleSchema = z.object({
  when: z.enum(["gte", "lte"]),
  value: DecimalStringSchema,
  tone: ApMetricToneSchema,
});

/** One org-configurable "big number" tile. */
export const ApMetricDefinitionSchema = z.object({
  id: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, {
      message: "Must be a lowercase slug (a-z, 0-9, hyphens)",
    }),
  /** Org-defined display label — data, rendered untranslated */
  label: z.string().min(1).max(80),
  /** Which value renders as the big number; the other is the sub-line */
  display: z.enum(["count", "amount"]),
  /** Fallback tone when no color rule matches */
  tone: ApMetricToneSchema.prefault("default"),
  /** Value-driven overrides of `tone`, first match wins */
  colorRules: z.array(ApMetricColorRuleSchema).max(6).optional(),
  source: ApMetricSourceSchema,
  /** Omit for unbounded backlog tiles */
  timeWindow: ApMetricTimeWindowSchema.optional(),
});

/** A metric definition together with its computed values. */
export const ApMetricValueSchema = z.object({
  definition: ApMetricDefinitionSchema,
  /**
   * Distinct invoices the metric resolves to, or tasks of the chosen type
   * for a `taskCount` tile.
   */
  count: z.int().nonnegative(),
  /**
   * Sum of those invoices' totals. `0.00` on a `taskCount` tile, which has
   * no dollar total.
   */
  amount: DecimalStringSchema,
  /** Tone after applying the definition's color rules to the value */
  tone: ApMetricToneSchema,
});

/** The exception behind an exception-derived drill-down row. */
export const ApMetricDrillExceptionSchema = z.object({
  /** Exception type name, e.g. "Duplicate invoice number" */
  typeLabel: z.string(),
  /** Owning user's name; null when unassigned or owned by an agent / workflow */
  ownerName: z.string().nullable(),
  /** Owner kind from the routing decision; null when unassigned */
  ownerType: z.string().nullable(),
  /** Hours since the exception was detected */
  ageHours: z.number().nonnegative(),
});

/** One invoice row in a metric drill-down. */
export const ApMetricDrillInvoiceSchema = z.object({
  invoiceId: z.uuid(),
  invoiceNumber: z.string().nullable(),
  supplierName: z.string().nullable(),
  status: z.enum(APInvoiceStatus),
  /** YYYY-MM-DD */
  dueDate: z.string().nullable(),
  amount: DecimalStringSchema,
  /** For tasks-derived metrics: the matched task to jump into */
  taskId: z.uuid().nullable(),
  /**
   * For exception-derived metrics: the invoice's oldest matching exception.
   * Null for other sources.
   */
  exception: ApMetricDrillExceptionSchema.nullable(),
});

/**
 * One open exception row (`ap_invoice_exceptions`). The drill-through is the
 * hand-off task when the evaluation had one wired; `reasonTag` carries the
 * exception type's Core Enum option id so the UI can group and filter.
 */
export const ApCommandCenterExceptionSchema = z.object({
  /** The exception row id */
  id: z.uuid(),
  /** The task handed to the owner, when one was wired — the drill-through */
  taskId: z.uuid().nullable(),
  /** Exception type (Core Enum option id) */
  reasonTag: z.string(),
  /** Exception type name, e.g. "Duplicate invoice number" */
  reasonLabel: z.string(),
  invoiceId: z.uuid(),
  invoiceNumber: z.string().nullable(),
  amount: DecimalStringSchema.nullable(),
  /** Owning user's name; null when the exception is unassigned or owned by an agent / workflow */
  ownerName: z.string().nullable(),
  /** Owner kind from the routing decision; null when unassigned */
  ownerType: z.string().nullable(),
  /** Hours since the exception was detected */
  ageHours: z.number().nonnegative(),
  /** The evaluator's reasoning — what a person must act on */
  actionRequired: z.string().nullable(),
});

export const GetApCommandCenterResponseSchema = z.object({
  /** The org's configured metric tiles with computed values, in order */
  metrics: z.array(ApMetricValueSchema),
  /** Open exceptions, oldest first */
  exceptions: z.array(ApCommandCenterExceptionSchema),
});

export const AP_METRIC_DRILL_PAGE_SIZE = 25;
/**
 * Offset pagination: the cap bounds the SQL OFFSET a caller can force. At the
 * default page size that is 25k invoices deep — beyond what anyone pages
 * through by hand; narrower tiles are the answer past that.
 */
export const AP_METRIC_DRILL_MAX_PAGE = 1000;

export const GetApCommandCenterInputSchema = z.object({
  range: ApCommandCenterRangeSchema.prefault(DEFAULT_AP_COMMAND_CENTER_RANGE),
});

export const ApMetricDrillDownInputSchema = z.object({
  metricId: z.string().min(1).max(64),
  /** 1-based page; invoices are ordered by amount, largest first */
  page: z.int().min(1).max(AP_METRIC_DRILL_MAX_PAGE).prefault(1),
  pageSize: z.int().min(1).max(100).prefault(AP_METRIC_DRILL_PAGE_SIZE),
  range: ApCommandCenterRangeSchema.prefault(DEFAULT_AP_COMMAND_CENTER_RANGE),
});

export const ApMetricDrillDownResponseSchema = z.object({
  definition: ApMetricDefinitionSchema,
  /** The requested page of invoices */
  invoices: z.array(ApMetricDrillInvoiceSchema),
  /** Total invoices behind the metric, across all pages */
  total: z.int().nonnegative(),
  page: z.int().positive(),
  pageSize: z.int().positive(),
});

export const GetApMetricsConfigResponseSchema = z.object({
  metrics: z.array(ApMetricDefinitionSchema),
  /** True when the org has no saved config and defaults are in effect */
  isDefault: z.boolean(),
});

export const SaveApMetricsConfigInputSchema = z.object({
  /** The full tile list in display order; null reverts to defaults */
  metrics: z
    .array(ApMetricDefinitionSchema)
    .min(1)
    .max(12)
    .nullable()
    .refine(
      (metrics) =>
        metrics === null ||
        new Set(metrics.map((m) => m.id)).size === metrics.length,
      { message: "Metric ids must be unique" },
    ),
});

export const apCommandCenterContracts = {
  get: oc
    .input(GetApCommandCenterInputSchema)
    .output(GetApCommandCenterResponseSchema)
    .route({
      method: "GET",
      summary: "Get the AP command center dashboard",
      description:
        "Returns the organization's AP metric tiles computed for a time range (7 days, month to date, 30 days, all), plus open invoice exceptions with owner and required action.",
    }),
  drillDown: oc
    .input(ApMetricDrillDownInputSchema)
    .output(ApMetricDrillDownResponseSchema)
    .route({
      method: "GET",
      summary: "List invoices behind an AP metric",
      description:
        "Returns the paged invoices that make up one AP command center metric tile, largest amount first, for the chosen time range.",
    }),
  getMetricsConfig: oc.output(GetApMetricsConfigResponseSchema).route({
    method: "GET",
    summary: "Get AP dashboard metric settings",
    description:
      "Returns the organization's configured AP command center metric tiles in display order, and whether built-in defaults are in effect.",
  }),
  saveMetricsConfig: oc
    .input(SaveApMetricsConfigInputSchema)
    .output(GetApMetricsConfigResponseSchema)
    .route({
      method: "POST",
      summary: "Save AP dashboard metric settings",
      description:
        "Admin only: saves the full ordered list of AP command center metric tiles (up to 12), or resets to defaults when null.",
    }),
} as const;

// Type exports
export type ApCommandCenterRange = z.infer<typeof ApCommandCenterRangeSchema>;
export type ApMetricTimeWindow = z.infer<typeof ApMetricTimeWindowSchema>;
export type ApMetricTaskTimeWindow = Omit<ApMetricTimeWindow, "on"> & {
  on: (typeof AP_METRIC_TASK_TIME_WINDOW_FIELDS)[number];
};
export type ApMetricInvoiceTimeWindow = Omit<ApMetricTimeWindow, "on"> & {
  on: (typeof AP_METRIC_INVOICE_TIME_WINDOW_FIELDS)[number];
};
export type ApMetricTaskSource = z.infer<typeof ApMetricTaskSourceSchema>;
export type ApMetricTaskCountSource = z.infer<
  typeof ApMetricTaskCountSourceSchema
>;
export type ApMetricInvoiceSource = z.infer<typeof ApMetricInvoiceSourceSchema>;
export type ApMetricExceptionSource = z.infer<
  typeof ApMetricExceptionSourceSchema
>;
export type ApMetricExceptionTimeWindow = Omit<ApMetricTimeWindow, "on"> & {
  on: (typeof AP_METRIC_EXCEPTION_TIME_WINDOW_FIELDS)[number];
};
export type ApMetricSource = z.infer<typeof ApMetricSourceSchema>;
export type ApMetricTone = z.infer<typeof ApMetricToneSchema>;
export type ApMetricColorRule = z.infer<typeof ApMetricColorRuleSchema>;
export type ApMetricDefinition = z.infer<typeof ApMetricDefinitionSchema>;
export type ApMetricValue = z.infer<typeof ApMetricValueSchema>;
export type ApMetricDrillException = z.infer<
  typeof ApMetricDrillExceptionSchema
>;
export type ApMetricDrillInvoice = z.infer<typeof ApMetricDrillInvoiceSchema>;
export type ApCommandCenterException = z.infer<
  typeof ApCommandCenterExceptionSchema
>;
export type GetApCommandCenterResponse = z.infer<
  typeof GetApCommandCenterResponseSchema
>;
export type ApMetricDrillDownResponse = z.infer<
  typeof ApMetricDrillDownResponseSchema
>;
export type GetApMetricsConfigResponse = z.infer<
  typeof GetApMetricsConfigResponseSchema
>;
